/**
 * Hand-written API wrappers for the async "Save Changes" version-generation
 * flow used by the Teammate Studio editor.
 *
 * When a user saves prompt-affecting edits, the backend generates the system
 * prompt, runs the compliance check, generates the changelog, and auto-creates
 * the new AgentVersion in a Celery task. The job is persisted, so an in-flight
 * generation survives a page refresh — these endpoints let the client start,
 * poll, and re-attach to a job.
 *
 * The job row always lives in the editing workspace's tenant schema (even for
 * public agents), so all three endpoints are mounted under `/tenant/`. The
 * `is_public` flag in the start body selects the schema the resulting version
 * is written into.
 */
import api from '@/api';

export type VersionGenerationStatus = 'pending' | 'generating' | 'ready' | 'blocked' | 'failed';

export interface VersionGenerationCompliance {
  action?: string;
  is_blocking?: boolean;
  rationale?: string;
  /** Compliance-suggested rewrite of the prompt, when the verdict is AMEND. */
  amendment?: string | null;
  /**
   * Which resolutions the backend will accept for this verdict. BLOCK returns
   * only `save_as_draft` — it is the one verdict with no proceed path.
   */
  allowed_decisions?: ComplianceResolveDecision[];
  /** Prompt sections the suggested rewrite changed, i.e. what needs editing. */
  flagged_sections?: string[];
  /** Present once an admin has resolved the gate (see resolveVersionGenerationJob). */
  resolution?: {
    decision?: ComplianceResolveDecision;
    original_action?: string;
    resolved_by?: string | null;
    resolved_at?: string;
  } | null;
  [key: string]: unknown;
}

export interface VersionGenerationJob {
  id: string;
  agent_id: string;
  agent_name: string;
  is_public: boolean;
  /** The result is kept as an inactive draft instead of becoming the active version. */
  save_as_draft: boolean;
  status: VersionGenerationStatus;
  result_version_id: string | null;
  version_label: string | null;
  content: string | null;
  changelog: string | null;
  compliance: VersionGenerationCompliance | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface StartVersionGenerationBody {
  is_public: boolean;
  /** Save the generated version as an inactive draft (approved later via finalise). */
  save_as_draft?: boolean;
  /** The generate-prompt inputs (role_name, missions, tools, attachments, ...). */
  generate: Record<string, unknown>;
  /** The version-create body (model, tool flags, ontology, attachment/memory ids, ...). */
  version: Record<string, unknown>;
}

export async function startVersionGeneration(
  agentId: string,
  body: StartVersionGenerationBody
): Promise<VersionGenerationJob> {
  const res = await api.post(`/api/v1/tenant/${agentId}/versions/generate/`, body);
  return res.data as VersionGenerationJob;
}

export async function getVersionGenerationJob(agentId: string, jobId: string): Promise<VersionGenerationJob> {
  const res = await api.get(`/api/v1/tenant/${agentId}/versions/generate/${jobId}/`);
  return res.data as VersionGenerationJob;
}

export async function listActiveVersionGenerations(): Promise<VersionGenerationJob[]> {
  const res = await api.get(`/api/v1/tenant/version-generations/active/`);
  return (res.data ?? []) as VersionGenerationJob[];
}

/**
 * How an admin resolves a compliance-gated save. Same vocabulary as the Slack
 * compliance gate, plus `save_as_draft` for BLOCK:
 *
 * - `accept_changes`  — save the compliance agent's suggested rewrite
 * - `override_submit` — acknowledge the flag and save the prompt as written
 * - `save_as_draft`   — BLOCK only: keep the work, but it can never be activated
 *
 * All three are audited with the original verdict.
 */
export type ComplianceResolveDecision = 'accept_changes' | 'override_submit' | 'save_as_draft';

export async function resolveVersionGenerationJob(
  agentId: string,
  jobId: string,
  decision: ComplianceResolveDecision
): Promise<VersionGenerationJob> {
  const res = await api.post(`/api/v1/tenant/${agentId}/versions/generate/${jobId}/resolve/`, { decision });
  return res.data as VersionGenerationJob;
}

export const TERMINAL_GENERATION_STATUSES: VersionGenerationStatus[] = ['ready', 'blocked', 'failed'];

export function isTerminalGenerationStatus(status: VersionGenerationStatus): boolean {
  return TERMINAL_GENERATION_STATUSES.includes(status);
}
